import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useUserScope } from '@/hooks/useUserScope';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Download, Plus, FolderPlus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { convertToCSV, downloadFile, formatDateForFilename } from '@/lib/export-utils';
import {
  ListBuilderContact,
  ListBuilderFilters,
  useExportListBuilder,
  useExistingCampaigns,
  useCreateCampaignWithContacts,
  useAddToCampaign,
} from '@/hooks/useListBuilder';

interface ListBuilderActionsPanelProps {
  selectedContacts: ListBuilderContact[];
  selectedCount: number;
  filters: ListBuilderFilters;
  totalCount: number;
  onClearSelection: () => void;
}

export function ListBuilderActionsPanel({
  selectedContacts,
  selectedCount,
  filters,
  totalCount,
  onClearSelection,
}: ListBuilderActionsPanelProps) {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [campaignName, setCampaignName] = useState('');
  const [platform, setPlatform] = useState('linkedin');
  const [territory, setTerritory] = useState('');
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [senderId, setSenderId] = useState('');

  const { personaId } = useUserScope();
  const { user } = useAuth();
  const exportMutation = useExportListBuilder();
  const { data: existingCampaigns } = useExistingCampaigns();
  const createCampaign = useCreateCampaignWithContacts();
  const addToCampaign = useAddToCampaign();

  const { data: sendingPersonas } = useQuery({
    queryKey: ['sending-personas'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sending_personas')
        .select('id, full_name')
        .order('full_name');
      if (error) throw error;
      return data;
    },
    enabled: createDialogOpen && !personaId,
  });

  const handleExportSelected = () => {
    if (selectedContacts.length === 0) {
      toast.error('No contacts selected');
      return;
    }
    exportContacts(selectedContacts, 'selected');
  };

  const handleExportAll = async () => {
    try {
      const data = await exportMutation.mutateAsync(filters);
      exportContacts(data, 'all');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export contacts');
    }
  };

  const exportContacts = (contacts: ListBuilderContact[], label: string) => {
    const exportData = contacts.map((c) => ({
      'Full Name': c.full_name || '',
      'First Name': c.first_name || '',
      'Last Name': c.last_name || '',
      'Job Title': c.job_title || '',
      'Company': c.company_name || '',
      'Domain': c.company_domain || '',
      'Country': c.company_country || '',
      'Email': c.email || '',
      'Email Status': c.email_status || '',
      'Email Verified': c.email_verified ? 'Yes' : 'No',
      'LinkedIn URL': c.linkedin_url || '',
      'Seniority': c.seniority_level || '',
      'Department': c.department || '',
      'Buyer Segment': c.buyer_segment || '',
      'Category': c.company_category || '',
      'ICP Tier': c.company_icp_tier || '',
      'Region': c.company_region || '',
      'Modalities': (c.company_modalities || []).join('; '),
      'Clinical Phase': c.company_clinical_phase || '',
      'Account Status': (c as any).company_account_tier || '',
      'Employee Range': c.company_employee_range || '',
      'Funding Status': c.company_funding_status || '',
      'Sales Rep': c.company_sales_rep || '',
      'Match Type': c.company_match_type || '',
      'Domain Status': c.company_domain_status || '',
      'Campaigns': c.campaign_count ?? 0,
      'Active Campaigns': c.active_campaign_count ?? 0,
      'Last Campaign Date': c.last_campaign_date ? new Date(c.last_campaign_date).toLocaleDateString() : '',
    }));

    const csv = convertToCSV(exportData);
    const filename = `list_builder_${label}_${formatDateForFilename()}.csv`;
    downloadFile(csv, filename, 'text/csv;charset=utf-8;');
    toast.success(`Exported ${contacts.length} contacts`);
  };

  const handleCreateCampaign = async () => {
    if (!campaignName.trim()) {
      toast.error('Campaign name required');
      return;
    }
    if (selectedContacts.length === 0) {
      toast.error('No contacts selected');
      return;
    }
    const resolvedPersonaId = personaId || senderId || null;
    if (!resolvedPersonaId) {
      toast.error('Please select a sender');
      return;
    }

    try {
      await createCampaign.mutateAsync({
        campaignName,
        platform,
        territory: territory || undefined,
        contacts: selectedContacts,
        sendingPersonaId: resolvedPersonaId,
        createdByUserId: user?.id || null,
      });
      toast.success(`Campaign "${campaignName}" created with ${selectedContacts.length} contacts`);
      setCreateDialogOpen(false);
      setCampaignName('');
      setPlatform('linkedin');
      setTerritory('');
      onClearSelection();
    } catch (err: any) {
      toast.error(err.message || 'Failed to create campaign');
    }
  };

  const handleAddToCampaign = async () => {
    if (!selectedCampaignId) {
      toast.error('Select a campaign');
      return;
    }
    if (selectedContacts.length === 0) {
      toast.error('No contacts selected');
      return;
    }

    try {
      await addToCampaign.mutateAsync({
        campaignId: selectedCampaignId,
        contacts: selectedContacts,
      });
      const campaignLabel = (existingCampaigns as any)?.find((c: any) => c.id === selectedCampaignId)?.name || 'campaign';
      toast.success(`Added ${selectedContacts.length} contacts to "${campaignLabel}"`);
      setAddDialogOpen(false);
      setSelectedCampaignId('');
      onClearSelection();
    } catch (err: any) {
      toast.error(err.message || 'Failed to add to campaign');
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 p-4 bg-card rounded-lg border">
      <Button variant="outline" size="sm" onClick={handleExportSelected} disabled={selectedCount === 0}>
        <Download className="h-4 w-4 mr-2" />
        Export Selected ({selectedCount})
      </Button>

      <Button variant="outline" size="sm" onClick={handleExportAll} disabled={exportMutation.isPending || totalCount === 0}>
        <Download className="h-4 w-4 mr-2" />
        {exportMutation.isPending ? (
          <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Exporting...</>
        ) : (
          `Export All (${totalCount.toLocaleString()})`
        )}
      </Button>

      <div className="h-6 w-px bg-border mx-1" />

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogTrigger asChild>
          <Button size="sm" disabled={selectedCount === 0}>
            <Plus className="h-4 w-4 mr-2" />
            New Campaign ({selectedCount})
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Campaign with {selectedCount} Contacts</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Campaign Name</Label>
              <Input value={campaignName} onChange={(e) => setCampaignName(e.target.value)} placeholder="Enter campaign name" />
            </div>
            <div className="space-y-2">
              <Label>Platform</Label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="multi-channel">Multi-Channel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Territory (optional)</Label>
              <Input value={territory} onChange={(e) => setTerritory(e.target.value)} placeholder="e.g. East, West, EU" />
            </div>
            {!personaId && sendingPersonas && sendingPersonas.length > 0 && (
              <div className="space-y-2">
                <Label>Assigned Sender *</Label>
                <Select value={senderId} onValueChange={setSenderId}>
                  <SelectTrigger><SelectValue placeholder="Select sender..." /></SelectTrigger>
                  <SelectContent>
                    {sendingPersonas.map((sp) => (
                      <SelectItem key={sp.id} value={sp.id}>{sp.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateCampaign} disabled={createCampaign.isPending}>
              {createCampaign.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating...</> : 'Create Campaign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" disabled={selectedCount === 0}>
            <FolderPlus className="h-4 w-4 mr-2" />
            Add to Campaign
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add {selectedCount} Contacts to Campaign</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Campaign</Label>
              <Select value={selectedCampaignId} onValueChange={setSelectedCampaignId}>
                <SelectTrigger><SelectValue placeholder="Choose a campaign..." /></SelectTrigger>
                <SelectContent>
                  {(existingCampaigns as any)?.map((c: any) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} ({c.platform || 'N/A'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddToCampaign} disabled={addToCampaign.isPending || !selectedCampaignId}>
              {addToCampaign.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Adding...</> : 'Add to Campaign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
